import { useEffect, useState } from 'react'
import {
  getLifeAreaBudgets,
  upsertLifeAreaBudget,
  LIFE_AREAS,
  type LifeAreaBudget,
} from '../../services/prioritizationService'
import { BRAND } from '../../config/brand'
import { SettingRow, SettingGroup } from '../settings/SettingRow'
import { countWord } from '../../utils/settingsWords'

/**
 * Budgeted-importance editor. Each life area gets a share of importance
 * points; items compete WITHIN their area for that share, so no single task
 * can independently look "most important." This is the unlock that keeps the
 * engine from treating everything as critical.
 *
 * Renders only when the engine is enabled (the budgets API returns [] when
 * off), so it is safe to mount anywhere in settings.
 *
 * It used to be eight rows of two number boxes with a Save button on each —
 * eight Save buttons in one card, on a page that had five more. It is now one
 * row per area on the same edit-in-place pattern as the rest of Settings
 * (owner 2026-09-07: one experience across the app).
 */
export function LifeAreaBudgetsEditor() {
  const [budgets, setBudgets] = useState<Record<string, LifeAreaBudget>>({})
  const [loaded, setLoaded] = useState(false)
  const [dPoints, setDPoints] = useState(50)
  const [dMax, setDMax] = useState(0)

  useEffect(() => {
    getLifeAreaBudgets().then((rows) => {
      const map: Record<string, LifeAreaBudget> = {}
      for (const b of rows) map[b.life_area] = b
      setBudgets(map)
      setLoaded(true)
    })
  }, [])

  const valueFor = (area: string): LifeAreaBudget =>
    budgets[area] ?? { life_area: area, importance_points: 50, max_active: 0 }

  const saveArea = async (area: string) => {
    const next: LifeAreaBudget = {
      life_area: area,
      importance_points: Math.max(0, dPoints),
      max_active: Math.max(0, dMax),
    }
    await upsertLifeAreaBudget(next)
    setBudgets((prev) => ({ ...prev, [area]: next }))
  }

  if (!loaded) return null

  /** "50 points, up to 3 at a time" — or the honest version when uncapped. */
  const reading = (b: LifeAreaBudget) =>
    b.max_active > 0
      ? `${b.importance_points} points, up to ${countWord(b.max_active, 'thing')} at a time`
      : `${b.importance_points} points, no cap`

  return (
    <SettingGroup
      title="Life-area importance"
      blurb={
        `Things compete for attention inside their own area, not against the whole list, ` +
        `so one busy area cannot crowd out the rest. Points are relative \u2014 an area on 100 ` +
        `carries twice the weight of one on 50. A cap limits how many things from that area ` +
        `${BRAND.assistant} will have you working on at once; no cap means no limit.`
      }
    >
      {LIFE_AREAS.map((area) => {
        const b = valueFor(area)
        return (
          <SettingRow
            key={area}
            label={area.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())}
            value={reading(b)}
            assumed={!budgets[area]}
            width="w-80"
            testId={`budget-${area}`}
            onOpen={() => {
              setDPoints(b.importance_points)
              setDMax(b.max_active)
            }}
            onSave={() => saveArea(area)}
          >
            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-app-micro text-secondary">
                  Share of importance
                </span>
                <input
                  type="number"
                  min={0}
                  value={dPoints}
                  onChange={(e) => setDPoints(Number(e.target.value) || 0)}
                  className="w-full rounded-lg border border-soft bg-surface px-3 py-2 text-app-body text-primary focus:border-brand focus:outline-none"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-app-micro text-secondary">
                  Most things at once in this area
                </span>
                <input
                  type="number"
                  min={0}
                  value={dMax}
                  onChange={(e) => setDMax(Number(e.target.value) || 0)}
                  className="w-full rounded-lg border border-soft bg-surface px-3 py-2 text-app-body text-primary focus:border-brand focus:outline-none"
                />
                <span className="mt-1 block text-app-micro text-muted">
                  Zero means no cap on this area.
                </span>
              </label>
            </div>
          </SettingRow>
        )
      })}
    </SettingGroup>
  )
}
